import styled from 'styled-components';
import { FlexRowMiddle } from './Flex';
import { IconButton } from './Icon';
import { Text } from './Text';
import { KeyboardEvent, MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import IconEdit from '../../../public/icons/edit.svg?react';
import IconClose from '../../../public/icons/close.svg?react';
import { sleep } from '../../scripts/scripts';

const StyledEditCell = styled(FlexRowMiddle)`
	max-width: 75%;
	min-width: 150px;
	width: 100%;
	height: 100%;
	overflow: hidden;
`;

const StyledInput = styled.input`
	min-width: 0;
	flex: 1;
	height: 100%;

	border: none;
	outline: none;
	padding: 0 0.5rem;

	color: ${({ theme }) => theme.text_desc_main};
	font-size: 1.3rem;
	font-weight: 400;
	font-family: ${({ theme }) => theme.FONTS_PRIMARY};

	background: ${({ theme }) => theme.input_bg};
	border-bottom: 2px solid ${({ theme }) => theme.text_theme};

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`;

const StyledSelect = styled.select`
	max-width: 80px;
	min-width: 0;
	flex: 1;
	height: 100%;

	border: none;
	outline: none;
	padding: 0 0.5rem;
	color: ${({ theme }) => theme.text_desc_main};
	font-size: 1.3rem;
	font-weight: 400;
	font-family: ${({ theme }) => theme.FONTS_PRIMARY};

	background: ${({ theme }) => theme.input_bg};
	border-bottom: 2px solid ${({ theme }) => theme.text_theme};

	color-scheme: dark;

	option {
		background-color: black;
		color-scheme: dark;
	}

	&:disabled {
		opacity: 0.4;
		cursor: not-allowed;
	}
`;

export function EditCellCurrency({
	children,
	value,
	valueSelect,
	options,
	saveValues,
	onInputMap,
	onBlurMap,
	disabled,
}: {
	children: ReactNode;
	value: string;
	options: { value: string; text: string }[];
	valueSelect: string;
	saveValues: (input: string, select: string) => Promise<void>;
	onInputMap: (input: string) => string | undefined;
	onBlurMap: (input: string) => string | undefined;
	disabled?: boolean;
}) {
	const [editInput, setEditInput] = useState<string>('');
	const [editSelect, setSelectInput] = useState<string>('');

	const [saving, setSaving] = useState<boolean>(false);

	const [editing, setEditing] = useState<boolean>(false);
	const editingRef = useRef<boolean>(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const onClickEdit = () => {
		setEditing(true);
		editingRef.current = true;
		setEditInput(value);
		setSelectInput(valueSelect);
	};

	const onClickCancel = (e: MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		setEditing(false);
		editingRef.current = false;
	};

	const onBlur = useCallback(async () => {
		await sleep(100);
		if (!editingRef.current) return;
		if (editInput && editSelect && onBlurMap && onBlurMap?.(editInput) === undefined) {
			return;
		}
		setSaving(true);
		if (!editInput || !editSelect) {
			await saveValues('', '');
		} else {
			await saveValues(editInput, editSelect);
		}
		setEditing(false);
		setSaving(false);
		editingRef.current = false;
	}, [editInput, editSelect, onBlurMap, saveValues]);

	const onInput = (input: string) => {
		if (onInputMap && onInputMap(input) === undefined) return;
		setEditInput(input);
	};

	const onKeyUp = (e: KeyboardEvent) => {
		if (e.key === 'Enter') {
			onBlur();
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: globalThis.MouseEvent) => {
			if (editingRef.current && containerRef.current && !containerRef.current.contains(event.target! as Node)) {
				onBlur();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [containerRef, onBlur]);

	return (
		<StyledEditCell ref={containerRef}>
			{!editing && (
				<>
					<Text $color="text_desc_main" $size="1.3rem" $weight="400">
						{children}
					</Text>
					<IconButton
						$colorFill="text_desc_main"
						$colorFillHover="text_theme"
						onClick={() => onClickEdit()}
						style={{ marginLeft: '1rem', height: 14, cursor: 'pointer' }}
						disabled={disabled}
					>
						<IconEdit />
					</IconButton>
				</>
			)}
			{editing && (
				<>
					<StyledSelect value={editSelect} onChange={(v) => setSelectInput(v.target.value)} disabled={saving}>
						{options.map((option, index) => (
							<option value={option.value} key={`currency_option_${index}`}>
								{option.text}
							</option>
						))}
					</StyledSelect>
					<StyledInput
						ref={inputRef}
						value={editInput}
						onChange={(v) => onInput(v.target.value)}
						disabled={saving}
						onKeyUp={onKeyUp}
					/>
					<IconButton
						$colorFill="text_desc_main"
						$colorFillHover="text_theme"
						onClick={onClickCancel}
						disabled={saving || disabled}
						style={{ marginLeft: '1rem', height: 14, cursor: 'pointer' }}
					>
						<IconClose />
					</IconButton>
				</>
			)}
		</StyledEditCell>
	);
}
